/**
 * Pattern Learning Engine
 * 
 * WHAT NOBODY ELSE IS DOING:
 * - Learns coding patterns from user accept/reject decisions
 * - Builds a personalized model of what "good code" looks like
 * - Identifies anti-patterns from rejected suggestions
 * - Continuously improves pattern matching over time
 */

import * as fs from 'fs';
import * as path from 'path';
import {
  PatternSignature,
  LearningFeedback,
  CodeSuggestion,
} from './types';

export interface LearnedPattern {
  id: string;
  pattern: string;
  type: 'positive' | 'negative'; // positive = good pattern, negative = anti-pattern
  category: PatternSignature['category'];
  confidence: number; // How confident we are this pattern matters
  examples: string[];
  frequency: number;
  acceptRate: number;
  rejectRate: number;
  lastSeen: string;
}

export interface LearningStats {
  totalFeedback: number;
  patternsLearned: number;
  positivePatterns: number;
  negativePatterns: number;
  accuracyImprovement: number; // % improvement since start
}

export class PatternLearner {
  private workspaceRoot: string;
  private patterns: LearnedPattern[] = [];
  private feedbackHistory: LearningFeedback[] = [];
  private patternsPath: string;
  private minFrequencyForPattern = 3; // Need to see pattern X times before learning

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
    this.patternsPath = path.join(workspaceRoot, '.guardrail', 'learned-patterns.json');
    this.loadPatterns();
  }

  /**
   * Record feedback and learn from it
   */
  learn(feedback: LearningFeedback): void {
    this.feedbackHistory.push(feedback);
    
    // Extract patterns from the code
    const codePatterns = this.extractPatterns(feedback.context);
    
    for (const extracted of codePatterns) {
      this.updatePattern(extracted, feedback.accepted, feedback.editedBefore);
    }

    // Prune low-value patterns periodically
    if (this.feedbackHistory.length % 50 === 0) {
      this.prunePatterns();
    }

    this.savePatterns();
  }

  /**
   * Extract patterns from code
   */
  private extractPatterns(code: string): Array<{ pattern: string; category: PatternSignature['category'] }> {
    const patterns: Array<{ pattern: string; category: PatternSignature['category'] }> = [];

    // Naming patterns
    patterns.push(...this.extractNamingPatterns(code));
    
    // Structure patterns
    patterns.push(...this.extractStructurePatterns(code));
    
    // Import patterns
    patterns.push(...this.extractImportPatterns(code));
    
    // Error handling patterns
    patterns.push(...this.extractErrorPatterns(code));
    
    // Async patterns
    patterns.push(...this.extractAsyncPatterns(code));
    
    // Testing patterns
    patterns.push(...this.extractTestingPatterns(code));

    // API patterns (new)
    patterns.push(...this.extractAPIPatterns(code));

    return patterns;
  }

  /**
   * Extract API usage patterns
   */
  private extractAPIPatterns(code: string): Array<{ pattern: string; category: 'api' }> {
    const patterns: Array<{ pattern: string; category: 'api' }> = [];

    // REST API patterns
    if (/fetch\s*\(\s*['"`]/.test(code)) {
      patterns.push({ pattern: 'fetch-api', category: 'api' });
    }
    if (/axios\.(get|post|put|patch|delete)\s*\(/.test(code)) {
      patterns.push({ pattern: 'axios-methods', category: 'api' });
    }

    // Express/API route patterns
    if (/\.(get|post|put|patch|delete)\s*\(\s*['"`]\//.test(code)) {
      patterns.push({ pattern: 'rest-route-handler', category: 'api' });
    }
    if (/router\.(get|post|put|patch|delete)/.test(code)) {
      patterns.push({ pattern: 'express-router', category: 'api' });
    }

    // Response handling
    if (/res\.(json|send|status|redirect)\s*\(/.test(code)) {
      patterns.push({ pattern: 'express-response', category: 'api' });
    }
    if (/\.json\(\)\s*\.then/.test(code) || /await\s+\w+\.json\(\)/.test(code)) {
      patterns.push({ pattern: 'json-parse-response', category: 'api' });
    }

    // Request validation
    if (/req\.(body|params|query|headers)/.test(code)) {
      patterns.push({ pattern: 'request-destructure', category: 'api' });
    }
    if (/zod|yup|joi|validator/.test(code)) {
      patterns.push({ pattern: 'validation-library', category: 'api' });
    }

    // Authentication patterns
    if (/bearer|authorization|jwt|token/i.test(code)) {
      patterns.push({ pattern: 'auth-header', category: 'api' });
    }
    if (/middleware|authenticate|isAuth|requireAuth/i.test(code)) {
      patterns.push({ pattern: 'auth-middleware', category: 'api' });
    }

    // Error response patterns
    if (/res\.status\s*\(\s*[45]\d{2}\s*\)/.test(code)) {
      patterns.push({ pattern: 'error-status-code', category: 'api' });
    }

    // Database query patterns
    if (/prisma\.\w+\.(find|create|update|delete|upsert)/.test(code)) {
      patterns.push({ pattern: 'prisma-query', category: 'api' });
    }
    if (/mongoose|\.find\(|\.findOne\(|\.save\(\)/.test(code)) {
      patterns.push({ pattern: 'mongoose-query', category: 'api' });
    }

    // GraphQL patterns
    if (/useQuery|useMutation|gql`/.test(code)) {
      patterns.push({ pattern: 'graphql-hooks', category: 'api' });
    }

    // WebSocket patterns
    if (/WebSocket|socket\.io|\.on\s*\(\s*['"]message['"]/.test(code)) {
      patterns.push({ pattern: 'websocket', category: 'api' });
    }

    return patterns;
  }

  /**
   * Extract naming convention patterns
   */
  private extractNamingPatterns(code: string): Array<{ pattern: string; category: 'naming' }> {
    const patterns: Array<{ pattern: string; category: 'naming' }> = [];

    // Function naming
    const funcRegex = /(?:function|const|let|var)\s+([a-zA-Z_]\w*)\s*(?:=\s*(?:async\s*)?\(|[\(:])/g;
    let match;
    while ((match = funcRegex.exec(code)) !== null) {
      const name = match[1];
      
      // Detect naming conventions
      if (/^handle[A-Z]/.test(name)) {
        patterns.push({ pattern: 'handle[A-Z]\\w+', category: 'naming' });
      } else if (/^on[A-Z]/.test(name)) {
        patterns.push({ pattern: 'on[A-Z]\\w+', category: 'naming' });
      } else if (/^get[A-Z]/.test(name)) {
        patterns.push({ pattern: 'get[A-Z]\\w+', category: 'naming' });
      } else if (/^set[A-Z]/.test(name)) {
        patterns.push({ pattern: 'set[A-Z]\\w+', category: 'naming' });
      } else if (/^is[A-Z]/.test(name)) {
        patterns.push({ pattern: 'is[A-Z]\\w+', category: 'naming' });
      } else if (/^has[A-Z]/.test(name)) {
        patterns.push({ pattern: 'has[A-Z]\\w+', category: 'naming' });
      } else if (/^use[A-Z]/.test(name)) {
        patterns.push({ pattern: 'use[A-Z]\\w+', category: 'naming' });
      } else if (/^fetch[A-Z]/.test(name)) {
        patterns.push({ pattern: 'fetch[A-Z]\\w+', category: 'naming' });
      } else if (/^load[A-Z]/.test(name)) {
        patterns.push({ pattern: 'load[A-Z]\\w+', category: 'naming' });
      } else if (/^create[A-Z]/.test(name)) {
        patterns.push({ pattern: 'create[A-Z]\\w+', category: 'naming' });
      } else if (/^update[A-Z]/.test(name)) {
        patterns.push({ pattern: 'update[A-Z]\\w+', category: 'naming' });
      } else if (/^delete[A-Z]/.test(name)) {
        patterns.push({ pattern: 'delete[A-Z]\\w+', category: 'naming' });
      }
      
      // Case style detection
      if (/^[a-z]+[A-Z]/.test(name)) {
        patterns.push({ pattern: 'camelCase', category: 'naming' });
      } else if (/^[A-Z][a-z]+[A-Z]/.test(name)) {
        patterns.push({ pattern: 'PascalCase', category: 'naming' });
      } else if (/^[a-z]+_[a-z]/.test(name)) {
        patterns.push({ pattern: 'snake_case', category: 'naming' });
      }
    }

    return patterns;
  }

  /**
   * Extract code structure patterns
   */
  private extractStructurePatterns(code: string): Array<{ pattern: string; category: 'structure' }> {
    const patterns: Array<{ pattern: string; category: 'structure' }> = [];

    // Arrow function vs regular function
    if (/const\s+\w+\s*=\s*\([^)]*\)\s*=>/.test(code)) {
      patterns.push({ pattern: 'arrow-function-assignment', category: 'structure' });
    }
    if (/function\s+\w+\s*\(/.test(code)) {
      patterns.push({ pattern: 'function-declaration', category: 'structure' });
    }

    // Destructuring patterns
    if (/const\s*\{[^}]+\}\s*=/.test(code)) {
      patterns.push({ pattern: 'object-destructuring', category: 'structure' });
    }
    if (/const\s*\[[^\]]+\]\s*=/.test(code)) {
      patterns.push({ pattern: 'array-destructuring', category: 'structure' });
    }

    // Template literals
    if (/`[^`]*\$\{[^}]+\}[^`]*`/.test(code)) {
      patterns.push({ pattern: 'template-literal', category: 'structure' });
    }

    // Spread operator
    if (/\.\.\.\w+/.test(code)) {
      patterns.push({ pattern: 'spread-operator', category: 'structure' });
    }

    // Optional chaining
    if (/\?\.[a-zA-Z]/.test(code)) {
      patterns.push({ pattern: 'optional-chaining', category: 'structure' });
    }

    // Nullish coalescing
    if (/\?\?/.test(code)) {
      patterns.push({ pattern: 'nullish-coalescing', category: 'structure' });
    }

    // Early returns
    if (/^\s*if\s*\([^)]+\)\s*return\b/m.test(code)) {
      patterns.push({ pattern: 'early-return', category: 'structure' });
    }

    // Guard clauses
    if (/if\s*\(\s*!\w+\s*\)\s*(?:return|throw)/.test(code)) {
      patterns.push({ pattern: 'guard-clause', category: 'structure' });
    }

    return patterns;
  }

  /**
   * Extract import patterns
   */
  private extractImportPatterns(code: string): Array<{ pattern: string; category: 'import' }> {
    const patterns: Array<{ pattern: string; category: 'import' }> = [];

    // Named imports
    if (/import\s*\{[^}]+\}\s*from/.test(code)) {
      patterns.push({ pattern: 'named-imports', category: 'import' });
    }

    // Default imports
    if (/import\s+\w+\s+from/.test(code)) {
      patterns.push({ pattern: 'default-import', category: 'import' });
    }

    // Namespace imports
    if (/import\s+\*\s+as\s+\w+\s+from/.test(code)) {
      patterns.push({ pattern: 'namespace-import', category: 'import' });
    }

    // Type-only imports
    if (/import\s+type\s+/.test(code)) {
      patterns.push({ pattern: 'type-only-import', category: 'import' });
    }

    // Relative imports
    if (/from\s+['"]\.\.?\//.test(code)) {
      patterns.push({ pattern: 'relative-import', category: 'import' });
    }

    // Absolute/alias imports
    if (/from\s+['"]@\//.test(code)) {
      patterns.push({ pattern: 'alias-import', category: 'import' });
    }

    return patterns;
  }

  /**
   * Extract error handling patterns
   */
  private extractErrorPatterns(code: string): Array<{ pattern: string; category: 'error_handling' }> {
    const patterns: Array<{ pattern: string; category: 'error_handling' }> = [];

    // Try-catch
    if (/try\s*\{[\s\S]*?\}\s*catch/.test(code)) {
      patterns.push({ pattern: 'try-catch', category: 'error_handling' });
    }

    // Try-catch-finally
    if (/try\s*\{[\s\S]*?\}\s*catch[\s\S]*?finally/.test(code)) {
      patterns.push({ pattern: 'try-catch-finally', category: 'error_handling' });
    }

    // Promise catch
    if (/\.catch\s*\(/.test(code)) {
      patterns.push({ pattern: 'promise-catch', category: 'error_handling' });
    }

    // Typed error handling
    if (/catch\s*\(\s*\w+:\s*\w+\s*\)/.test(code)) {
      patterns.push({ pattern: 'typed-catch', category: 'error_handling' });
    }

    // Error instanceof check
    if (/instanceof\s+\w*Error/.test(code)) {
      patterns.push({ pattern: 'error-instanceof', category: 'error_handling' });
    }

    // Custom error throw
    if (/throw\s+new\s+\w+Error/.test(code)) {
      patterns.push({ pattern: 'custom-error-throw', category: 'error_handling' });
    }

    // Error logging
    if (/console\.(error|warn)\s*\(/.test(code)) {
      patterns.push({ pattern: 'error-logging', category: 'error_handling' });
    }

    return patterns;
  }

  /**
   * Extract async patterns
   */
  private extractAsyncPatterns(code: string): Array<{ pattern: string; category: 'async' }> {
    const patterns: Array<{ pattern: string; category: 'async' }> = [];

    // Async/await
    if (/async\s+(?:function|\()/.test(code) || /\basync\s*\(/.test(code)) {
      patterns.push({ pattern: 'async-function', category: 'async' });
    }
    if (/\bawait\s+/.test(code)) {
      patterns.push({ pattern: 'await-usage', category: 'async' });
    }

    // Promise.all
    if (/Promise\.all\s*\(/.test(code)) {
      patterns.push({ pattern: 'promise-all', category: 'async' });
    }

    // Promise.allSettled
    if (/Promise\.allSettled\s*\(/.test(code)) {
      patterns.push({ pattern: 'promise-allSettled', category: 'async' });
    }

    // Promise.race
    if (/Promise\.race\s*\(/.test(code)) {
      patterns.push({ pattern: 'promise-race', category: 'async' });
    }

    // Async IIFE
    if (/\(\s*async\s*\(\s*\)\s*=>\s*\{/.test(code)) {
      patterns.push({ pattern: 'async-iife', category: 'async' });
    }

    // .then chain
    if (/\.then\s*\([^)]+\)\s*\.then/.test(code)) {
      patterns.push({ pattern: 'promise-chain', category: 'async' });
    }

    return patterns;
  }

  /**
   * Extract testing patterns
   */
  private extractTestingPatterns(code: string): Array<{ pattern: string; category: 'testing' }> {
    const patterns: Array<{ pattern: string; category: 'testing' }> = [];

    // describe blocks
    if (/describe\s*\(\s*['"]/.test(code)) {
      patterns.push({ pattern: 'describe-block', category: 'testing' });
    }

    // it/test blocks
    if (/(?:it|test)\s*\(\s*['"]/.test(code)) {
      patterns.push({ pattern: 'test-block', category: 'testing' });
    }

    // expect assertions
    if (/expect\s*\([^)]+\)\./.test(code)) {
      patterns.push({ pattern: 'expect-assertion', category: 'testing' });
    }

    // beforeEach/afterEach
    if (/(?:beforeEach|afterEach|beforeAll|afterAll)\s*\(/.test(code)) {
      patterns.push({ pattern: 'test-hooks', category: 'testing' });
    }

    // Mock functions
    if (/(?:jest|vi)\.(?:fn|mock|spyOn)\s*\(/.test(code)) {
      patterns.push({ pattern: 'mock-function', category: 'testing' });
    }

    // Async tests
    if (/(?:it|test)\s*\(\s*['"][^'"]+['"]\s*,\s*async/.test(code)) {
      patterns.push({ pattern: 'async-test', category: 'testing' });
    }

    return patterns;
  }

  /**
   * Update or create a pattern based on feedback
   */
  private updatePattern(
    extracted: { pattern: string; category: PatternSignature['category'] },
    accepted: boolean,
    edited: boolean
  ): void {
    let existing = this.patterns.find(p => p.pattern === extracted.pattern);

    if (!existing) {
      existing = {
        id: `pattern-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        pattern: extracted.pattern,
        type: 'positive',
        category: extracted.category,
        confidence: 0.5,
        examples: [],
        frequency: 0,
        acceptRate: 0,
        rejectRate: 0,
        lastSeen: new Date().toISOString(),
      };
      this.patterns.push(existing);
    }

    existing.frequency++;
    existing.lastSeen = new Date().toISOString();

    // Update accept/reject rates with exponential moving average
    const alpha = 0.1;
    if (accepted && !edited) {
      existing.acceptRate = existing.acceptRate * (1 - alpha) + 1 * alpha;
      existing.rejectRate = existing.rejectRate * (1 - alpha);
    } else if (accepted && edited) {
      existing.acceptRate = existing.acceptRate * (1 - alpha) + 0.5 * alpha;
      existing.rejectRate = existing.rejectRate * (1 - alpha) + 0.5 * alpha;
    } else {
      existing.acceptRate = existing.acceptRate * (1 - alpha);
      existing.rejectRate = existing.rejectRate * (1 - alpha) + 1 * alpha;
    }

    // Determine if pattern is positive or negative
    if (existing.frequency >= this.minFrequencyForPattern) {
      if (existing.acceptRate > 0.7) {
        existing.type = 'positive';
        existing.confidence = Math.min(1, existing.acceptRate);
      } else if (existing.rejectRate > 0.6) {
        existing.type = 'negative';
        existing.confidence = Math.min(1, existing.rejectRate);
      }
    }
  }

  /**
   * Prune low-value patterns
   */
  private prunePatterns(): void {
    this.patterns = this.patterns.filter(p => {
      // Keep patterns that are seen frequently
      if (p.frequency >= 10) return true;
      
      // Keep patterns with strong signal
      if (p.confidence > 0.7) return true;
      
      // Remove old patterns with no signal
      const lastSeen = new Date(p.lastSeen);
      const daysSinceLastSeen = (Date.now() - lastSeen.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceLastSeen > 30 && p.frequency < 5) return false;
      
      return true;
    });
  }

  /**
   * Score a suggestion based on learned patterns
   */
  scorePatterns(suggestion: CodeSuggestion): { score: number; matchedPatterns: LearnedPattern[] } {
    const codePatterns = this.extractPatterns(suggestion.code);
    const matchedPatterns: LearnedPattern[] = [];
    let positiveScore = 0;
    let negativeScore = 0;
    let totalWeight = 0;

    for (const extracted of codePatterns) {
      const learned = this.patterns.find(p => p.pattern === extracted.pattern);
      if (learned && learned.frequency >= this.minFrequencyForPattern) {
        matchedPatterns.push(learned);
        const weight = learned.confidence * Math.log(learned.frequency + 1);
        totalWeight += weight;
        
        if (learned.type === 'positive') {
          positiveScore += weight;
        } else {
          negativeScore += weight;
        }
      }
    }

    if (totalWeight === 0) {
      return { score: 50, matchedPatterns }; // Neutral if no patterns match
    }

    const score = ((positiveScore - negativeScore) / totalWeight + 1) * 50;
    return { score: Math.max(0, Math.min(100, Math.round(score))), matchedPatterns };
  }

  /**
   * Get suggestions for improving code based on learned patterns
   */
  getSuggestions(code: string): string[] {
    const suggestions: string[] = [];
    const extracted = this.extractPatterns(code);
    const extractedSet = new Set(extracted.map(e => e.pattern));

    // Find highly positive patterns not in code
    const positivePatterns = this.patterns
      .filter(p => p.type === 'positive' && p.confidence > 0.7 && !extractedSet.has(p.pattern))
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 3);

    for (const pattern of positivePatterns) {
      suggestions.push(`Consider using ${pattern.pattern} pattern (${Math.round(pattern.acceptRate * 100)}% acceptance rate)`);
    }

    // Find negative patterns in code
    const negativeMatches = this.patterns.filter(p => 
      p.type === 'negative' && p.confidence > 0.6 && extractedSet.has(p.pattern)
    );

    for (const pattern of negativeMatches) {
      suggestions.push(`Consider avoiding ${pattern.pattern} pattern (${Math.round(pattern.rejectRate * 100)}% rejection rate)`);
    }

    return suggestions;
  }

  /**
   * Get learning statistics
   */
  getStats(): LearningStats {
    const positivePatterns = this.patterns.filter(p => p.type === 'positive' && p.frequency >= this.minFrequencyForPattern);
    const negativePatterns = this.patterns.filter(p => p.type === 'negative' && p.frequency >= this.minFrequencyForPattern);

    // Calculate accuracy improvement (simplified metric)
    const recentFeedback = this.feedbackHistory.slice(-100);
    const earlyFeedback = this.feedbackHistory.slice(0, 100);
    
    const recentAcceptRate = recentFeedback.length > 0 
      ? recentFeedback.filter(f => f.accepted).length / recentFeedback.length 
      : 0;
    const earlyAcceptRate = earlyFeedback.length > 0 
      ? earlyFeedback.filter(f => f.accepted).length / earlyFeedback.length 
      : 0;
    
    const accuracyImprovement = earlyAcceptRate > 0 
      ? ((recentAcceptRate - earlyAcceptRate) / earlyAcceptRate) * 100 
      : 0;

    return {
      totalFeedback: this.feedbackHistory.length,
      patternsLearned: positivePatterns.length + negativePatterns.length,
      positivePatterns: positivePatterns.length,
      negativePatterns: negativePatterns.length,
      accuracyImprovement: Math.round(accuracyImprovement),
    };
  }

  /**
   * Get all learned patterns
   */
  getPatterns(): LearnedPattern[] {
    return this.patterns.filter(p => p.frequency >= this.minFrequencyForPattern);
  }

  /**
   * Get positive patterns (good patterns to follow)
   */
  getPositivePatterns(): LearnedPattern[] {
    return this.patterns
      .filter(p => p.type === 'positive' && p.frequency >= this.minFrequencyForPattern)
      .sort((a, b) => b.confidence - a.confidence);
  }

  /**
   * Get negative patterns (anti-patterns to avoid)
   */
  getNegativePatterns(): LearnedPattern[] {
    return this.patterns
      .filter(p => p.type === 'negative' && p.frequency >= this.minFrequencyForPattern)
      .sort((a, b) => b.confidence - a.confidence);
  }

  /**
   * Export patterns for backup or sharing
   */
  exportPatterns(): string {
    return JSON.stringify({
      patterns: this.patterns,
      stats: this.getStats(),
      exportedAt: new Date().toISOString(),
    }, null, 2);
  }

  /**
   * Import patterns from backup
   */
  importPatterns(data: string): void {
    try {
      const imported = JSON.parse(data);
      if (imported.patterns && Array.isArray(imported.patterns)) {
        this.patterns = imported.patterns;
        this.savePatterns();
      }
    } catch (e) {
      console.error('Failed to import patterns:', e);
    }
  }

  private loadPatterns(): void {
    try {
      if (fs.existsSync(this.patternsPath)) {
        const data = JSON.parse(fs.readFileSync(this.patternsPath, 'utf-8'));
        this.patterns = data.patterns || [];
        this.feedbackHistory = data.feedbackHistory || [];
      }
    } catch (e) {
      console.error('Failed to load patterns:', e);
    }
  }

  private savePatterns(): void {
    try {
      const dir = path.dirname(this.patternsPath);
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
      }
      
      fs.writeFileSync(this.patternsPath, JSON.stringify({
        patterns: this.patterns,
        feedbackHistory: this.feedbackHistory.slice(-1000), // Keep last 1000 feedbacks
        lastUpdated: new Date().toISOString(),
      }, null, 2));
    } catch (e) {
      console.error('Failed to save patterns:', e);
    }
  }
}
